import { useSelector } from 'react-redux'

import { selectAuthenticationTypes } from 'redux/2FA/2FA-select'
import { selectFeatures } from 'redux/settings/settings-select'
import { selectRecentTransactions } from 'redux/transactions/transactions-select'
import { selectCompaniesRegistrationFee, selectCurrentUser, selectEnabledServices } from 'redux/user/user-select'
import { THEME_NAME } from 'utils/helperConfig'

let enabledComponentsThemePersonal = {}
let enabledComponentsThemeCompany = {}

const usePermissions = () => {
    const currentUser = useSelector(selectCurrentUser)
    const enabledServices = useSelector(selectEnabledServices)
    const recentTransactions = useSelector(selectRecentTransactions)
    const features = useSelector(selectFeatures)
    const companiesRegistrationFee = useSelector(selectCompaniesRegistrationFee)
    const authenticationTypes = useSelector(selectAuthenticationTypes)

    const {
        isCompany,
        kycStatus,
        status: userStatus,
        defaultTwoFactorAuthenticationType,
        isPhoneConfirmed,
        personalQuestionnaire,
        canCreateCompany,
        userRegistrationIntent,
        canOrderAssets,
        canRegisterForPaymentGateway,
        isAffiliateTermsAndConditionsAccepted,
    } = { ...currentUser }
    const { isRequired: isPersonalQuestionnaireRequired, isSaved: isPersonalQuestionnaireSaved } = { ...personalQuestionnaire }
    const { PlaygroundEnabled, AffiliatesEnabled, VirtualAddressEnabled, ReferralProgramEnabled, PreviewModeEnabled } = { ...features }
    const { Basic: XCurrency, DigitalAssets, LimitedToken, BankAccounts, CreditCards, CardAcquiring } = { ...enabledServices }
    const { isRequired: IsPaymentRequired, isPaid: isPaymentPaid } = { ...companiesRegistrationFee }
    const { available: availableAuthenticationTypes } = { ...authenticationTypes }

    const isCompanyRegistrationIntent = userRegistrationIntent === 'Company'
    const isPersonal = !isCompany
    const forceGoogleAuthenticator =
        availableAuthenticationTypes?.length === 1 && availableAuthenticationTypes?.some(o => o === 'GoogleAuthenticator')
    const isGoogleAuthenticator = defaultTwoFactorAuthenticationType === 'GoogleAuthenticator'

    const onboardingStatus = userStatus === undefined ? false : userStatus === 'Onboarding'

    const enableWizard =
        (isPhoneConfirmed === undefined ? false : isPersonal && !isPhoneConfirmed) ||
        (forceGoogleAuthenticator === undefined ? false : forceGoogleAuthenticator && !isGoogleAuthenticator && isPersonal) ||
        (kycStatus === undefined || userStatus === undefined
            ? false
            : isPersonal &&
              userStatus === 'Onboarding' &&
              (kycStatus === 'Pending' || kycStatus === 'Rejected' || kycStatus === 'KycReceived' || kycStatus === 'SentForKyc')) ||
        (isPersonalQuestionnaireRequired === undefined || isPersonalQuestionnaireSaved === undefined
            ? false
            : isPersonal && isPersonalQuestionnaireRequired && !isPersonalQuestionnaireSaved) ||
        (IsPaymentRequired === undefined || isPaymentPaid === undefined ? false : isCompany && IsPaymentRequired && !isPaymentPaid)

    const enabledComponents = {
        Onboarding: onboardingStatus,
        EnableWizard: enableWizard,
        PlaygroundEnabled: !!PlaygroundEnabled,
        XCurrency: !!XCurrency,
        DigitalAssets: !!DigitalAssets,
        LimitedToken: !!LimitedToken,
        BankAccounts: !!BankAccounts,
        CreditCards: !!CreditCards,
        CardAcquiring: !!CardAcquiring,
        SettingsTabsDocuments: !onboardingStatus && !enableWizard,
        SettingsTabsNotifications: !onboardingStatus && !enableWizard,
        SettingsTabsAffiliate: !onboardingStatus && !enableWizard && (AffiliatesEnabled || !!isAffiliateTermsAndConditionsAccepted),
        SettingsTabsServices: !onboardingStatus && !enableWizard,
        SettingsShippingAddress: !onboardingStatus && !enableWizard,
        SettingsVerifyRegistrationAddress: !onboardingStatus && !enableWizard,
        OpenBusinessAccount: !onboardingStatus && !enableWizard && !isCompany && canCreateCompany,
        OrderAdressCard: !onboardingStatus && !enableWizard && VirtualAddressEnabled,
        ZeroBalances: !onboardingStatus && !enableWizard,
        AvailableAssestsList: !onboardingStatus && !enableWizard,
        CanOrderAssets: !onboardingStatus && !enableWizard && canOrderAssets,
        GoogleAuthenticatorWarning: !forceGoogleAuthenticator && !isGoogleAuthenticator,
        ForceGoogleAuthenticator: forceGoogleAuthenticator,
        PreviewModeEnabled: (onboardingStatus || enableWizard) && isPhoneConfirmed && PreviewModeEnabled,
    }

    const enabledComponentsCompany = {
        ...enabledComponents,
        SettingsTabsUsers: !onboardingStatus && !enableWizard,
        CardAcquiring: !onboardingStatus && !enableWizard && !!CardAcquiring,
        RecentTransactions: !!recentTransactions && recentTransactions?.totalCount > 0,
        AccountsDropDown: (onboardingStatus && !isCompanyRegistrationIntent) || !onboardingStatus,
        CanRegisterForPaymentGateway: canRegisterForPaymentGateway === undefined ? true : canRegisterForPaymentGateway,
        ReferralProgram: false,
        SwitchOnboardingIntent: onboardingStatus && isCompanyRegistrationIntent,
    }

    const enabledComponentsPersonal = {
        ...enabledComponents,
        RecentTransactions: !onboardingStatus && !enableWizard && !!recentTransactions && recentTransactions?.totalCount > 0,
        CardAcquiring: false,
        AccountsDropDown:
            (onboardingStatus && !isCompanyRegistrationIntent) ||
            (!onboardingStatus && !forceGoogleAuthenticator) ||
            (!onboardingStatus && forceGoogleAuthenticator && isGoogleAuthenticator),
        SettingsTabsUsers: false,
        ReferralProgram: ReferralProgramEnabled,
        SwitchOnboardingIntent: false,
    }

    enabledComponentsThemePersonal = {
        ...enabledComponentsPersonal,
    }

    enabledComponentsThemeCompany = {
        ...enabledComponentsCompany,
    }

    // This function determines whether a UI component should be enabled or
    // not based on user type (isCompany or personal).

    // Simplified version of the function:
    /*
    const getPermission = componentId => {
        const theme = isCompany ? enabledComponentsThemeCompany : enabledComponentsThemePersonal
        return theme?.[componentId] !== undefined ? !!theme[componentId] : true
    }
     */
    const getPermission = data => {

        if (isCompany) {
            if (typeof enabledComponentsThemeCompany?.[data] !== 'undefined') {
                return !!enabledComponentsThemeCompany?.[data]
            } else {
                return true
            }
        } else {
            if (typeof enabledComponentsThemePersonal?.[data] !== 'undefined') {
                return !!enabledComponentsThemePersonal?.[data]
            } else {
                return true
            }
        }
    }

    return { getPermission }
}

export { usePermissions }
